import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import {
  ArrowRight, CheckCircle2, XCircle, ChevronDown, Layers,
  Shield, TrendingUp, Coins, Crown,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AdvisorCard from "@/components/AdvisorCard";
import MedicareWaitlist from "@/components/MedicareWaitlist";
import { PRODUCT_ICON_MAP } from "@/components/ProductCard";
import { PRODUCT_PRIMARY_SERVICE, SERVICE_NAME } from "@/lib/serviceMeta";
import usePageSEO from "@/hooks/usePageSEO";

const LAYER_META = {
  protect: { label: "Protect", Icon: Shield },
  accumulate: { label: "Accumulate", Icon: TrendingUp },
  distribute: { label: "Distribute", Icon: Coins },
  transfer: { label: "Transfer", Icon: Crown },
};

function Pill({ children, testId }) {
  return (
    <span data-testid={testId} className="lw-pill mr-2 mb-2 inline-flex">
      {children}
    </span>
  );
}

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-8">
      <span className="lw-eyebrow">{eyebrow}</span>
      <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl lg:text-4xl text-lw-navy leading-[1.1]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-base text-lw-muted leading-relaxed max-w-2xl">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      data-testid={`faq-item-${idx}`}
      className="border-b border-lw-navy/10 py-4"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-start justify-between w-full text-left gap-4"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-sans-ui font-semibold text-lw-navy">{q}</span>
        <ChevronDown
          size={18}
          className={`flex-shrink-0 mt-1 text-lw-navy/60 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 text-sm text-lw-muted leading-relaxed">{a}</p>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a] = await Promise.all([
          apiGet(`/products/${slug}`),
          apiGet("/advisors"),
        ]);
        if (cancelled) return;
        setProduct(p);
        setAdvisors(a || []);
      } catch (e) {
        if (!cancelled) setError("This product could not be found.");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  usePageSEO(product ? {
    title: product.name,
    description: product.tagline,
    jsonLd: [
      { "@context": "https://schema.org", "@type": "Product", name: product.name, description: product.tagline, category: product.category },
      product.faqs?.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: product.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) } : null,
    ].filter(Boolean),
  } : { title: "Solution", description: "Lifespan Wealth Partners product." });

  if (error) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="max-w-3xl mx-auto px-5 pt-32 pb-20">
          <h1 className="font-serif-display text-3xl text-lw-navy">Not found.</h1>
          <p className="mt-3 text-lw-muted">{error}</p>
          <Link to="/" className="btn-gold mt-6 inline-flex">Back home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="max-w-3xl mx-auto px-5 pt-32 pb-20">
          <p className="text-lw-muted">Loading…</p>
        </main>
        <Footer />
      </div>
    );
  }

  const Icon = PRODUCT_ICON_MAP[product.id] || Shield;
  const LayerIcon = LAYER_META[product.wealth_pyramid_layer]?.Icon || Layers;
  const layerLabel = LAYER_META[product.wealth_pyramid_layer]?.label || "Strategy";
  const applyHref = `/apply?source=product&products=${product.id}`;
  const matchedAdvisors = (product.matched_advisor_ids || [])
    .map((id) => advisors.find((a) => a.id === id))
    .filter(Boolean);

  return (
    <div data-testid={`product-page-${product.id}`} className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">

        {/* Hero band */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-start gap-5">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, rgba(11,37,69,0.08) 0%, rgba(212,162,76,0.18) 100%)",
                color: "#0B2545",
              }}
              aria-hidden="true"
            >
              <Icon size={26} />
            </div>
            <div className="flex-1 min-w-0">
              {PRODUCT_PRIMARY_SERVICE[product.id] && (
                <Link
                  to={`/services/${PRODUCT_PRIMARY_SERVICE[product.id]}`}
                  data-testid="product-parent-service"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-lw-navy/60 hover:text-lw-navy transition-colors"
                >
                  Part of: {SERVICE_NAME[PRODUCT_PRIMARY_SERVICE[product.id]]} →
                </Link>
              )}
              <span className="lw-eyebrow" style={{ marginTop: 10 }}>Solution</span>
              <h1
                data-testid="product-headline"
                className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.05]"
              >
                {product.name}
              </h1>
              <p className="mt-4 text-lg text-lw-muted leading-relaxed max-w-2xl">
                {product.tagline}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Pill testId="age-pill">Ages {product.age_range_min}–{product.age_range_max}</Pill>
                <span
                  data-testid="layer-badge"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-lw-navy/90 text-lw-ivory text-xs font-semibold uppercase tracking-[0.14em]"
                >
                  <LayerIcon size={13} /> {layerLabel}
                </span>
                {product.complexity !== "foundational" && (
                  <Pill testId="complexity-pill">{product.complexity}</Pill>
                )}
                {product.is_available === false && (
                  <span
                    data-testid="product-coming-soon-badge"
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-lw-navy text-lw-ivory text-xs font-semibold uppercase tracking-[0.14em]"
                  >
                    {product.availability_status || "Coming Soon"}
                  </span>
                )}
              </div>

              {product.is_available === false && (
                <div className="mt-6" data-testid="product-waitlist-wrapper">
                  <MedicareWaitlist
                    context={{ product_id: product.id }}
                    label={product.id === "medicare" || product.id === "medicare-part-d" ? undefined : `${product.name} — coming soon`}
                    description={product.id === "medicare" || product.id === "medicare-part-d" ? undefined : `${product.name} offerings are being onboarded.`}
                  />
                </div>
              )}

              {/* SEC/FINRA securities banner — Variable Annuity + VUL only */}
              {(product.id === "variable-annuity" || product.id === "variable-universal-life") && (
                <div
                  data-testid="securities-banner"
                  className="mt-6 rounded-2xl border-2 border-red-800/40 bg-red-50 p-5 sm:p-6"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-red-900">
                    ⚠️ Variable products are securities
                  </p>
                  <p className="mt-2 text-sm text-red-950/90 leading-relaxed">
                    Sold BY PROSPECTUS ONLY through appropriately-registered
                    broker-dealer representatives. Involve investment risk
                    INCLUDING POSSIBLE LOSS OF PRINCIPAL. Sub-account values
                    fluctuate. Fees include M&E charges, administrative fees,
                    sub-account expenses, and optional rider fees.{" "}
                    <Link
                      to="/compliance#variable-products"
                      className="underline font-semibold hover:text-red-900"
                    >
                      Read the full disclosure &rarr;
                    </Link>
                  </p>
                </div>
              )}

              {product.is_available !== false && (
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to={applyHref} data-testid="product-cta-primary" className="btn-gold">
                    Talk to an advisor about this <ArrowRight size={18} />
                  </Link>
                  <Link to="/tools/recommender" data-testid="product-cta-secondary" className="btn-ghost-navy">
                    Try the recommender first
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <SectionHeading eyebrow="How it works" title={`The ${product.name} mechanics`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {product.how_it_works.map((s, i) => (
              <div key={i} data-testid={`how-step-${i}`} className="rounded-2xl border border-lw-navy/10 bg-white p-6">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-lw-gold/20 text-lw-navy font-serif-display font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <h3 className="font-serif-display text-lg text-lw-navy">{s.title}</h3>
                </div>
                <p className="mt-3 text-sm text-lw-muted leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who it's for + not ideal */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div data-testid="who-for" className="rounded-2xl bg-white border border-lw-navy/10 p-6 sm:p-8">
            <SectionHeading eyebrow="Who it's for" title="A strong fit when…" />
            <ul className="space-y-3">
              {product.who_for.map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-lw-navy/85">
                  <CheckCircle2 size={18} className="text-lw-gold mt-0.5 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="not-ideal-for" className="rounded-2xl bg-lw-ivory-warm border border-lw-navy/10 p-6 sm:p-8">
            <SectionHeading eyebrow="Who it's NOT ideal for" title="Honest disqualifiers" />
            <ul className="space-y-3">
              {product.not_ideal_for.map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-lw-navy/85">
                  <XCircle size={18} className="text-lw-navy/40 mt-0.5 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Scenarios */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <SectionHeading
            eyebrow="Real-world scenarios"
            title="What this looks like in practice"
            subtitle="Hypothetical illustrations showing how this product fits typical situations."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {product.scenarios.map((s, i) => (
              <div
                key={i}
                data-testid={`scenario-${i}`}
                className="rounded-2xl border border-lw-gold/30 bg-white p-6 sm:p-7 shadow-[0_30px_80px_-40px_rgba(11,37,69,0.18)]"
              >
                <h3 className="font-serif-display text-xl text-lw-navy">{s.name}</h3>
                <p className="mt-2 text-sm text-lw-muted leading-relaxed">{s.narrative}</p>
                <div className="mt-4 border-t border-lw-navy/10 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {s.numbers.map((n, j) => (
                    <div key={j} className="flex items-baseline justify-between gap-3">
                      <span className="text-xs text-lw-muted">{n.label}</span>
                      <span className="font-serif-display text-base text-lw-navy tabular-nums">{n.value}</span>
                    </div>
                  ))}
                </div>
                <p data-testid={`scenario-hypothetical-${i}`} className="mt-4 text-[11px] italic text-lw-muted leading-relaxed">
                  {product.hypothetical_footnote}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison */}
        {product.comparisons && (
          <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
            <SectionHeading
              eyebrow="Compare"
              title={product.comparisons.title}
            />
            <div className="overflow-x-auto rounded-2xl border border-lw-navy/10 bg-white">
              <table data-testid="comparison-table" className="w-full text-sm">
                <thead>
                  <tr className="bg-lw-ivory-warm">
                    <th className="text-left p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">Feature</th>
                    {product.comparisons.columns.map((c) => (
                      <th key={c} className="text-left p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {product.comparisons.rows.map((row, i) => (
                    <tr key={i} className="border-t border-lw-navy/5">
                      <td className="p-4 font-semibold text-lw-navy">{row.feature}</td>
                      {row.values.map((v, j) => (
                        <td key={j} className="p-4 text-lw-navy/80">{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <SectionHeading eyebrow="FAQ" title="Common questions, plainly answered" />
          <div data-testid="faqs">
            {product.faqs.map((f, i) => (
              <FAQItem key={i} idx={i} q={f.q} a={f.a} />
            ))}
          </div>
        </section>

        {/* Process */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <SectionHeading eyebrow="How we structure this" title="A 4-step process, no pressure" />
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {product.process_steps.map((s, i) => (
              <li key={i} data-testid={`process-step-${i}`} className="rounded-2xl border border-lw-navy/10 bg-white p-5">
                <div className="text-lw-gold font-serif-display text-xl">0{i + 1}</div>
                <h3 className="mt-2 font-serif-display text-lg text-lw-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-lw-muted leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Matched advisors */}
        {matchedAdvisors.length > 0 && (
          <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
            <SectionHeading
              eyebrow="Specialist match"
              title={matchedAdvisors.length === 1 ? "Your specialist advisor" : "Your specialist advisors"}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
              {matchedAdvisors.slice(0, 2).map((a) => (
                <AdvisorCard key={a.id} advisor={a} />
              ))}
            </div>
          </section>
        )}

        {/* Final CTA — hidden for Coming Soon products (waitlist card in hero handles it) */}
        {product.is_available !== false && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <div className="rounded-3xl bg-lw-navy text-lw-ivory p-8 sm:p-12 relative overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(212,162,76,0.18) 0%, rgba(11,37,69,0) 70%)" }}
            />
            <div className="relative max-w-2xl">
              <span className="text-xs uppercase tracking-[0.2em] text-lw-gold font-semibold">Ready when you are</span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl leading-[1.1]">
                Ready to explore {product.name}? Book a no-obligation 30-min discovery call.
              </h2>
              <p className="mt-3 text-sm sm:text-base text-lw-ivory/70 leading-relaxed">
                We&apos;ll discuss whether this is the right fit, model the numbers, and compare 2–3 carriers — no pressure, ever.
              </p>
              <Link to={applyHref} data-testid="product-cta-bottom" className="btn-gold mt-6 inline-flex">
                Start the conversation <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
        )}

        {/* Compliance microcopy */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 mt-12 space-y-4">
          <p
            data-testid="product-disclaimer"
            className="text-xs text-lw-muted leading-relaxed"
          >
            {product.product_disclaimer}
          </p>
          {product.regulatory_notes && (
            <div
              data-testid="product-regulatory-notes"
              className="rounded-2xl border border-lw-navy/10 bg-white p-5 sm:p-6"
            >
              <span className="lw-eyebrow" style={{ marginBottom: 6 }}>
                Regulatory notes
              </span>
              <p className="mt-2 text-xs text-lw-muted leading-relaxed">
                {product.regulatory_notes}
              </p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
